/**
 * VFX type definitions.
 *
 * Defines JSON-serializable schemas for VFX particles and emitters.
 */
import * as THREE from 'three';

import { EngineClass } from '../systems/ClassRegistry.js';
import { Resource } from '../utils/ResourceManager.js';
import { property } from '../utils/serialization/decorator.js';

export enum AppearanceMode {
  Square = 0,
  Circular = 1,
}

export enum RenderModeEnum {
  StretchBillboard = 'stretchBillboard',
  Billboard = 'billboard',
  Mesh = 'mesh',
}

export interface ShadingHooks {
  customUniforms?: string;
  customVaryings?: string;
  vertexBeforeMain?: string;
  vertexBeforeOutput?: string;
  fragmentBeforeMain?: string;
  fragmentBeforeOutput?: string;
}

export type EaseFunction =
  | 'easeLinear'
  // Power1
  | 'easeInPower1'
  | 'easeOutPower1'
  | 'easeInOutPower1'
  // Power2
  | 'easeInPower2'
  | 'easeOutPower2'
  | 'easeInOutPower2'
  // Power3
  | 'easeInPower3'
  | 'easeOutPower3'
  | 'easeInOutPower3'
  // Power4
  | 'easeInPower4'
  | 'easeOutPower4'
  | 'easeInOutPower4'
  // Quad
  | 'easeInQuad'
  | 'easeOutQuad'
  | 'easeInOutQuad'
  // Cubic
  | 'easeInCubic'
  | 'easeOutCubic'
  | 'easeInOutCubic'
  // Quart
  | 'easeInQuart'
  | 'easeOutQuart'
  | 'easeInOutQuart'
  // Quint
  | 'easeInQuint'
  | 'easeOutQuint'
  | 'easeInOutQuint'
  // Sine
  | 'easeInSine'
  | 'easeOutSine'
  | 'easeInOutSine'
  // Expo
  | 'easeInExpo'
  | 'easeOutExpo'
  | 'easeInOutExpo'
  // Circ
  | 'easeInCirc'
  | 'easeOutCirc'
  | 'easeInOutCirc'
  // Elastic
  | 'easeInElastic'
  | 'easeOutElastic'
  | 'easeInOutElastic'
  // Back
  | 'easeInBack'
  | 'easeOutBack'
  | 'easeInOutBack'
  // Bounce
  | 'easeInBounce'
  | 'easeOutBounce'
  | 'easeInOutBounce';

export const easeFunctionList: EaseFunction[] = [
  'easeLinear',
  'easeInPower1',
  'easeOutPower1',
  'easeInOutPower1',
  'easeInPower2',
  'easeOutPower2',
  'easeInOutPower2',
  'easeInPower3',
  'easeOutPower3',
  'easeInOutPower3',
  'easeInPower4',
  'easeOutPower4',
  'easeInOutPower4',
  'easeInQuad',
  'easeOutQuad',
  'easeInOutQuad',
  'easeInCubic',
  'easeOutCubic',
  'easeInOutCubic',
  'easeInQuart',
  'easeOutQuart',
  'easeInOutQuart',
  'easeInQuint',
  'easeOutQuint',
  'easeInOutQuint',
  'easeInSine',
  'easeOutSine',
  'easeInOutSine',
  'easeInExpo',
  'easeOutExpo',
  'easeInOutExpo',
  'easeInCirc',
  'easeOutCirc',
  'easeInOutCirc',
  'easeInElastic',
  'easeOutElastic',
  'easeInOutElastic',
  'easeInBack',
  'easeOutBack',
  'easeInOutBack',
  'easeInBounce',
  'easeOutBounce',
  'easeInOutBounce',
];

/**
 * JSON schema for VFXParticles settings
 */
@EngineClass('VFXParticlesSettings')
export class VFXParticlesSettings {
  /** Maximum number of particles */
  @property()
    nbParticles?: number;
  /** Brightness multiplier */
  @property()
    intensity?: number;
  /** How particles are rendered (`RenderModeEnum` string values). */
  @property()
    renderMode?: 'stretchBillboard' | 'billboard' | 'mesh';
  /** Stretch factor for stretchBillboard mode */
  @property()
    stretchScale?: number;
  /** Size fade in/out range (0-1 of lifetime) - exactly 2 values [start, end] */
  @property()
    fadeSize?: [number, number];
  /** Opacity fade in/out range - exactly 2 values [start, end] */
  @property()
    fadeAlpha?: [number, number];
  /** Gravity force applied to particles [x, y, z] */
  @property()
    gravity?: [number, number, number];
  /** Particle billboarding / alpha mask shape; normalized to `AppearanceMode` for GPU. */
  @property()
    appearance?: 'square' | 'circular';
  /** Easing function for particle animations */
  @property()
    easeFunction?: EaseFunction;
  /** Blending preset; normalized to `THREE.Blending` for materials. */
  @property()
    blendingMode?: 'normal' | 'additive' | 'subtractive' | 'multiply';
  /** Path to alpha texture for particles */
  @property()
    alphaMapPath?: string;
  /** Path to custom geometry model for particles */
  @property()
    geometryPath?: string;
  /** Custom GLSL shader hooks for advanced rendering effects */
  @property()
    shadingHooks?: ShadingHooks;
  /** Override face side */
  side?: THREE.Side;
  /** Use depth test */
  @property()
    depthTest?: boolean;
}

/**
 * JSON schema for VFXEmitter settings
 */
@EngineClass('VFXEmitterSettings')
export class VFXEmitterSettings {
  /** Index of particle system to use (for v2+, links to particles array) */
  @property()
    particlesIndex?: number;
  /** Continuously emit particles */
  @property()
    loop?: boolean;
  /** Emission cycle duration in seconds */
  @property()
    duration?: number;
  /** Number of particles to emit per cycle */
  @property()
    nbParticles?: number;
  /** How particles are spawned */
  @property()
    spawnMode?: 'time' | 'burst';
  /** Time delay before starting emission */
  @property()
    delay?: number;
  /** Particle lifetime range [min, max] */
  @property()
    particlesLifetime?: [number, number];
  /** Minimum start position [x, y, z] */
  @property()
    startPositionMin?: [number, number, number];
  /** Maximum start position [x, y, z] */
  @property()
    startPositionMax?: [number, number, number];
  /** Minimum start rotation [x, y, z] */
  @property()
    startRotationMin?: [number, number, number];
  /** Maximum start rotation [x, y, z] */
  @property()
    startRotationMax?: [number, number, number];
  /** Minimum rotation speed [x, y, z] */
  @property()
    rotationSpeedMin?: [number, number, number];
  /** Maximum rotation speed [x, y, z] */
  @property()
    rotationSpeedMax?: [number, number, number];
  /** Minimum emission direction [x, y, z] */
  @property()
    directionMin?: [number, number, number];
  /** Maximum emission direction [x, y, z] */
  @property()
    directionMax?: [number, number, number];
  /** Particle size range [min, max] */
  @property()
    size?: [number, number];
  /** Particle speed range [min, max] */
  @property()
    speed?: [number, number];
  /** Colors at start (randomly selected) */
  @property()
    colorStart?: string[];
  /** Colors at end (randomly selected) */
  @property()
    colorEnd?: string[];
  /** Use emitter's local space for directions */
  @property()
    useLocalDirection?: boolean;
}

/**
 * JSON representation of a VFX definition
 */
export interface VFXDefinitionJSON {
  /** Schema version */
  version?: 1;
  /** Unique name for this VFX */
  name: string;
  /** Particle system settings array */
  particles: VFXParticlesSettings[];
  /** Emitter settings array */
  emitters: VFXEmitterSettings[];
}

/**
 * Complete VFX definition with versioning and serialization
 */
@EngineClass('VFXDefinition', { isResource: true, resourcePrefix: 'vfx' })
export class VFXDefinition extends Resource {
  static readonly CURRENT_VERSION = 1;

  @property()
    version: number = 1;
  @property()
    name: string = '';
  @property()
    particles: VFXParticlesSettings[] = [];
  @property()
    emitters: VFXEmitterSettings[] = [];

  /**
   * Serialize to JSON
   */
  toJSON(): VFXDefinitionJSON {
    return {
      version: 1,
      name: this.name,
      particles: this.particles,
      emitters: this.emitters,
    };
  }

  /**
   * Deserialize from JSON
   */
  static fromJSON(json: VFXDefinitionJSON): VFXDefinition {
    VFXDefinition.validate(json);
    const definition = new VFXDefinition();
    definition.version = 1;
    definition.name = json.name;
    definition.particles = json.particles.map((particle) => Object.assign(new VFXParticlesSettings(), particle));
    definition.emitters = json.emitters.map((emitter) => Object.assign(new VFXEmitterSettings(), emitter));
    return definition;
  }

  /**
   * Validate VFX definition structure and values
   */
  private static validate(json: VFXDefinitionJSON): void {
    if (!json.name || typeof json.name !== 'string') {
      throw new Error('VFXDefinition: name is required and must be a string');
    }

    if (!Array.isArray(json.particles) || json.particles.length === 0) {
      throw new Error(`VFXDefinition "${json.name}": particles array is required and must have at least one entry`);
    }

    if (!Array.isArray(json.emitters) || json.emitters.length === 0) {
      throw new Error(`VFXDefinition "${json.name}": emitters array is required and must have at least one entry`);
    }

    // Validate each particle system
    json.particles.forEach((p, index) => {
      if (!p || typeof p !== 'object') {
        throw new Error(`VFXDefinition "${json.name}": particles[${index}] must be an object`);
      }

      if (p.nbParticles !== undefined && (p.nbParticles < 1 || !Number.isInteger(p.nbParticles))) {
        throw new Error(`VFXDefinition "${json.name}": particles[${index}].nbParticles must be a positive integer`);
      }

      if (p.fadeSize !== undefined && (!Array.isArray(p.fadeSize) || p.fadeSize.length !== 2)) {
        throw new Error(`VFXDefinition "${json.name}": particles[${index}].fadeSize must be an array of exactly 2 numbers`);
      }

      if (p.fadeAlpha !== undefined && (!Array.isArray(p.fadeAlpha) || p.fadeAlpha.length !== 2)) {
        throw new Error(`VFXDefinition "${json.name}": particles[${index}].fadeAlpha must be an array of exactly 2 numbers`);
      }

      if (p.gravity !== undefined && (!Array.isArray(p.gravity) || p.gravity.length !== 3)) {
        throw new Error(`VFXDefinition "${json.name}": particles[${index}].gravity must be an array of exactly 3 numbers [x, y, z]`);
      }
    });

    // Validate each emitter
    json.emitters.forEach((e, index) => {
      if (!e || typeof e !== 'object') {
        throw new Error(`VFXDefinition "${json.name}": emitters[${index}] must be an object`);
      }

      if (e.particlesIndex !== undefined) {
        if (!Number.isInteger(e.particlesIndex) || e.particlesIndex < 0 || e.particlesIndex >= json.particles.length) {
          throw new Error(`VFXDefinition "${json.name}": emitters[${index}].particlesIndex must be a valid index (0-${json.particles.length - 1})`);
        }
      }

      if (e.duration !== undefined && e.duration < 0) {
        throw new Error(`VFXDefinition "${json.name}": emitters[${index}].duration must be non-negative`);
      }

      if (e.nbParticles !== undefined && (e.nbParticles < 1 || !Number.isInteger(e.nbParticles))) {
        throw new Error(`VFXDefinition "${json.name}": emitters[${index}].nbParticles must be a positive integer`);
      }

      if (e.delay !== undefined && e.delay < 0) {
        throw new Error(`VFXDefinition "${json.name}": emitters[${index}].delay must be non-negative`);
      }

      if (e.particlesLifetime !== undefined && (!Array.isArray(e.particlesLifetime) || e.particlesLifetime.length !== 2)) {
        throw new Error(`VFXDefinition "${json.name}": emitters[${index}].particlesLifetime must be an array of exactly 2 numbers [min, max]`);
      }

      // Validate vector arrays (must be length 3)
      const vec3Fields = ['startPositionMin', 'startPositionMax', 'startRotationMin', 'startRotationMax',
        'rotationSpeedMin', 'rotationSpeedMax', 'directionMin', 'directionMax'] as const;
      for (const field of vec3Fields) {
        const value = e[field];
        if (value !== undefined && (!Array.isArray(value) || value.length !== 3)) {
          throw new Error(`VFXDefinition "${json.name}": emitters[${index}].${field} must be an array of exactly 3 numbers [x, y, z]`);
        }
      }

      // Validate range arrays (must be length 2)
      if (e.size !== undefined && (!Array.isArray(e.size) || e.size.length !== 2)) {
        throw new Error(`VFXDefinition "${json.name}": emitters[${index}].size must be an array of exactly 2 numbers [min, max]`);
      }

      if (e.speed !== undefined && (!Array.isArray(e.speed) || e.speed.length !== 2)) {
        throw new Error(`VFXDefinition "${json.name}": emitters[${index}].speed must be an array of exactly 2 numbers [min, max]`);
      }

      // Validate color arrays
      if (e.colorStart !== undefined && !Array.isArray(e.colorStart)) {
        throw new Error(`VFXDefinition "${json.name}": emitters[${index}].colorStart must be an array of color strings`);
      }

      if (e.colorEnd !== undefined && !Array.isArray(e.colorEnd)) {
        throw new Error(`VFXDefinition "${json.name}": emitters[${index}].colorEnd must be an array of color strings`);
      }
    });
  }
}
